import type * as React from "react";
import { cx } from "../internal/cx.js";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  type DialogSize,
  DialogTitle,
  DialogTrigger,
} from "../molecules/dialog.js";
import { FormActions } from "../molecules/form-actions.js";

/**
 * ModalForm — shared shell for add/invite/setup modals.
 *
 * Legacy add/edit components should not be copied wholesale. Domain packages
 * keep their controlled fields and compose them inside this shell so header,
 * body, footer, sizing, and mobile behavior stay consistent.
 */

export interface ModalFormProps extends Omit<React.FormHTMLAttributes<HTMLFormElement>, "title"> {
  /** Controlled open state. */
  open?: boolean;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  /** Controlled open-state callback. */
  onOpenChange?: (open: boolean) => void;
  /** Optional trigger, usually a Button. */
  trigger?: React.ReactNode;
  /** Modal title. */
  title: React.ReactNode;
  /** Supporting copy under the title. */
  description?: React.ReactNode;
  /** Form body. */
  children: React.ReactNode;
  /** Secondary action slot, usually Cancel / Back (link). */
  secondaryAction?: React.ReactNode;
  /** Primary action slot, usually Submit / Save (full-width). */
  primaryAction?: React.ReactNode;
  /** Fully custom footer. Overrides primary/secondary actions. Pass FormActions when customizing. */
  footer?: React.ReactNode;
  /** Desktop max-width. */
  size?: DialogSize;
  /** Hide the default close button. */
  hideCloseButton?: boolean;
  /** Accessible label for the close button. */
  closeLabel?: string;
}

export function ModalForm({
  children,
  className,
  closeLabel,
  defaultOpen,
  description,
  footer,
  hideCloseButton = false,
  onOpenChange,
  open,
  primaryAction,
  secondaryAction,
  size = "md",
  title,
  trigger,
  ...formProps
}: ModalFormProps) {
  return (
    <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent closeLabel={closeLabel} hideCloseButton={hideCloseButton} size={size}>
        <form className={cx("cw-v2-modal-form", className)} {...formProps}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          <DialogBody>{children}</DialogBody>
          {footer || primaryAction || secondaryAction ? (
            <DialogFooter>
              {footer ?? (
                <FormActions variant="registration">
                  {primaryAction}
                  {secondaryAction}
                </FormActions>
              )}
            </DialogFooter>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
